// src/workspace-developer/workspaceDeveloperApi.ts
import { apiClient } from "@maat/api-base";
var workspaceDeveloperApi = {
  /**
   * The access state, the sandbox, the grantable scopes and whether issuance is
   * configured at all — one read, everything the console branches on.
   */
  status: async () => apiClient.get("/main/api/workspace/developer"),
  /**
   * The Developer Terms version in force, with its body and hash. The console renders
   * `bodyMarkdown` VERBATIM and posts `{ termsVersion, bodySha256 }` back from THIS
   * response — never a cached or hardcoded version. 503 when the document is unreadable.
   */
  terms: async () => apiClient.get("/main/api/workspace/developer/terms"),
  /** Ask Maat for developer access. Idempotent; re-asks after a decline. */
  request: async () => apiClient.post("/main/api/workspace/developer/request"),
  /** Sign the Terms. This is what opens key issuance. */
  signTerms: async (data) => apiClient.post("/main/api/workspace/developer/terms/sign", data),
  /** One tab's keys. Omit `environment` for both. Revoked rows are included. */
  listKeys: async (environment) => apiClient.get("/main/api/workspace/developer/keys", { params: { environment } }),
  /** Mint a key. The plaintext is in this response ONCE — not stored, not retrievable. */
  issueKey: async (data) => apiClient.post("/main/api/workspace/developer/keys", data),
  /** New key id and secret on the same row. The old key fails immediately. */
  rotateKey: async (keyId) => apiClient.post(`/main/api/workspace/developer/keys/${encodeURIComponent(keyId)}/rotate`),
  /** Terminal. Takes effect on the holder's very next request. */
  revokeKey: async (keyId) => apiClient.delete(`/main/api/workspace/developer/keys/${encodeURIComponent(keyId)}`),
  /**
   * The registered endpoint for ONE environment, or null. Omit `environment` for the
   * pre-switch behaviour (whichever of the pair exists).
   */
  webhookEndpoint: async (environment) => apiClient.get("/main/api/workspace/developer/webhook-endpoint", {
    params: { environment }
  }),
  // The endpoint's lifecycle from the console: register, delete, rotate and test, each
  // over the SAME service function the developer API runs, on ONE named environment.
  /** Register or replace the endpoint for one environment. The challenge runs inside this call. */
  registerWebhookEndpoint: async (data) => apiClient.put("/main/api/workspace/developer/webhook-endpoint", data),
  /** Stop delivering to one environment's endpoint. Keeps the history. Idempotent. */
  deleteWebhookEndpoint: async (environment) => apiClient.delete("/main/api/workspace/developer/webhook-endpoint", {
    params: { environment }
  }),
  /** Mint the next signing secret. The secret is in this response and nowhere else, ever. */
  rotateWebhookSecret: async (environment) => apiClient.post("/main/api/workspace/developer/webhook-endpoint/rotate-secret", void 0, {
    params: { environment }
  }),
  /** Queue ONE real delivery over a fixed fixture to one environment's endpoint. */
  sendTestWebhookEvent: async (data) => apiClient.post("/main/api/workspace/developer/webhook-endpoint/test", data),
  /** The Deliveries page's KPI strip, for one environment or both. */
  deliveryStats: async (environment) => apiClient.get("/main/api/workspace/developer/webhook-deliveries/stats", {
    params: { environment }
  }),
  /**
   * Delivery ENVELOPES — status, attempt, error code, `payload_present`. No payload.
   */
  deliveries: async (params) => apiClient.get("/main/api/workspace/developer/webhook-deliveries", { params }),
  /** Wipe this workspace's own sandbox. Takes no id — there is only one. */
  resetSandbox: async () => apiClient.post("/main/api/workspace/developer/sandbox/reset"),
  // The inbound request log. No export and no delete — the retention cron owns the only
  // deletion this table sees. `params` goes straight through as the query string, and
  // every key must be DECLARED in the backend schema: `validate()` MERGES the query
  // rather than replacing it, so an undeclared key reaches the handler unvalidated.
  requests: async (params) => apiClient.get("/main/api/workspace/developer/requests", { params }),
  // `stats` before the `:requestId` read, mirroring the router's literal-path-first
  // order so the two files stay checkable against each other.
  requestStats: async (params) => apiClient.get("/main/api/workspace/developer/requests/stats", { params }),
  /** Requests per UTC day (or hour) for the Overview chart, zero-filled. */
  requestSeries: async (params) => apiClient.get("/main/api/workspace/developer/requests/series", { params }),
  // `requestDetail`, not `request` — that name is taken by the unrelated
  // developer-ACCESS request (`POST …/request`).
  requestDetail: async (requestId) => apiClient.get(`/main/api/workspace/developer/requests/${encodeURIComponent(requestId)}`),
  // The console's half of the webhook lifecycle: same service functions, projections and
  // 404 as `GET /v1/webhooks/deliveries/{delivery_id}` and `POST …/redrive`. Two
  // implementations would be two answers to "may a recipient read this body".
  /** One delivery, WITH the body we sent. (`deliveryDetail`, mirroring `requestDetail`.) */
  deliveryDetail: async (deliveryId) => apiClient.get(
    `/main/api/workspace/developer/webhook-deliveries/${encodeURIComponent(deliveryId)}`
  ),
  /**
   * Put one delivery back in the queue — `developer.manage`, because it makes us POST to
   * a third party. Budgeted per acting MEMBER, on the same rolling 24-hour counter the
   * developer API's own redrive spends against.
   */
  redriveDelivery: async (deliveryId) => apiClient.post(
    `/main/api/workspace/developer/webhook-deliveries/${encodeURIComponent(deliveryId)}/redrive`
  )
};

export {
  workspaceDeveloperApi
};
